import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import { getAuthErrorMessage } from '../../utils/authErrorUtils';
import { ADMIN_PATHS, adminPath } from '../../constants/adminPaths';
import '../styles/supportLoginPage.css';

const SupportLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    admin,
    loading,
    signInWithEmailLink,
    verifyPendingSmsCode,
    getPendingSecondFactorInfo,
    signOut,
    pendingMagicLinkLogin,
  } = useAdminAuth();
  const { hasPermission, ADMIN_PERMISSIONS } = useAdminPermissions();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [secondFactorMethod, setSecondFactorMethod] = useState('');
  const [maskedDestination, setMaskedDestination] = useState('');
  const [awaitingAuthState, setAwaitingAuthState] = useState(false);

  const canAccessSupport = useMemo(() => {
    if (!admin) return false;
    return (
      hasPermission(ADMIN_PERMISSIONS.SUPPORT.VIEW) ||
      hasPermission(ADMIN_PERMISSIONS.ADMIN.MANAGE)
    );
  }, [admin, hasPermission, ADMIN_PERMISSIONS]);

  const defaultLandingPath = useMemo(() => {
    if (!admin) return ADMIN_PATHS.SUPPORT;
    const canSeeSupport = hasPermission(ADMIN_PERMISSIONS.SUPPORT.VIEW);
    const canSeeDashboard = hasPermission(ADMIN_PERMISSIONS.DASHBOARD.VIEW);
    return canSeeSupport && !canSeeDashboard ? ADMIN_PATHS.SUPPORT : ADMIN_PATHS.DASHBOARD;
  }, [admin, hasPermission, ADMIN_PERMISSIONS]);

  const redirectTarget = useMemo(() => {
    const from = location?.state?.from;
    if (typeof from === 'string' && from.startsWith('/')) {
      return from;
    }
    return defaultLandingPath;
  }, [location, defaultLandingPath]);

  useEffect(() => {
    if (loading) return;
    if (!admin) return;

    // If we already have an admin/support user signed in, route them immediately.
    if (canAccessSupport) {
      navigate(adminPath(redirectTarget), { replace: true });
      return;
    }

    // If signed in but not allowed, immediately sign out and show a clear error.
    (async () => {
      try {
        await signOut();
      } catch {
        // ignore signout failures; we still want to show the auth error
      } finally {
        setError('Your account does not have support access. Please contact an administrator.');
      }
    })();
  }, [admin, canAccessSupport, loading, navigate, redirectTarget, signOut]);

  // After a submit, wait for onAuthStateChanged to populate `admin` before redirecting.
  useEffect(() => {
    if (!awaitingAuthState) return;
    if (loading) return;
    if (!admin) return;

    setAwaitingAuthState(false);

    if (canAccessSupport) {
      navigate(adminPath(redirectTarget), { replace: true });
      return;
    }

    (async () => {
      try {
        await signOut();
      } catch {
        // ignore
      } finally {
        setError('Your account does not have support access. Please contact an administrator.');
      }
    })();
  }, [admin, awaitingAuthState, canAccessSupport, loading, navigate, redirectTarget, signOut]);

  useEffect(() => {
    if (!pendingMagicLinkLogin) return;
    const pending = getPendingSecondFactorInfo();
    setSecondFactorMethod(pending?.secondFactorMethod === 'sms' ? 'sms' : pending?.secondFactorMethod === 'email' ? 'email' : '');
    setMaskedDestination(pending?.maskedDestination || '');
    setLinkSent(true);
  }, [pendingMagicLinkLogin, getPendingSecondFactorInfo]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pendingMagicLinkLogin) {
      const pending = getPendingSecondFactorInfo();
      setSecondFactorMethod(pending?.secondFactorMethod === 'sms' ? 'sms' : pending?.secondFactorMethod === 'email' ? 'email' : '');
      setMaskedDestination(pending?.maskedDestination || '');
      setLinkSent(true);
      return;
    }
    setError('');
    setLinkSent(false);
    setMaskedDestination('');
    setSubmitting(true);
    try {
      const res = await signInWithEmailLink(email.trim(), password);
      if (res?.magicLinkSent) {
        setSecondFactorMethod(res?.secondFactorMethod === 'sms' ? 'sms' : res?.secondFactorMethod === 'email' ? 'email' : '');
        setMaskedDestination(res?.maskedDestination || '');
        setLinkSent(true);
        return;
      }
      if (res?.passwordOnly) {
        setAwaitingAuthState(true);
      }
    } catch (err) {
      setLinkSent(false);
      setError(getAuthErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onVerifySms = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await verifyPendingSmsCode(smsCode);
      setAwaitingAuthState(true);
    } catch (err) {
      setLinkSent(false);
      setError(getAuthErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="supportLogin">
      <div className="supportLogin__bg" aria-hidden="true" />

      <main className="supportLogin__container">
        <section className="supportLogin__card" aria-label="Support login">
          <header className="supportLogin__header">
            <div className="supportLogin__badge" aria-hidden="true">
              Support
            </div>
            <h1 className="supportLogin__title">Sign in</h1>
            <p className="supportLogin__subtitle">
              For support employees and administrators.
            </p>
          </header>

          {error ? (
            <div className="supportLogin__alert" role="alert">
              <span className="supportLogin__alertDot" aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}

          {!error && linkSent ? (
            <div className="supportLogin__alert" role="status" style={{ borderColor: 'rgba(47, 124, 255, 0.35)', background: 'rgba(47, 124, 255, 0.12)' }}>
              <span className="supportLogin__alertDot" aria-hidden="true" style={{ background: 'var(--al-accent)' }} />
              <span>
                {secondFactorMethod === 'sms'
                  ? `SMS code sent${maskedDestination ? ` to ${maskedDestination}` : ''}. Enter it below to finish signing in.`
                  : secondFactorMethod === 'email'
                    ? 'Login link sent. Check your email and click the link to finish signing in. You can keep this page open — it will log you in automatically.'
                    : 'Verification request sent. Please wait for your second-factor prompt.'}
              </span>
            </div>
          ) : null}

          {linkSent && secondFactorMethod === 'sms' ? (
            <form className="supportLogin__form" onSubmit={onVerifySms}>
              <label className="supportLogin__label">
                <span className="supportLogin__labelText">SMS verification code</span>
                <input
                  className="supportLogin__input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={smsCode}
                  onChange={(e) => setSmsCode(String(e.target.value || '').replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  required
                />
              </label>

              <button className="supportLogin__submit" type="submit" disabled={submitting || awaitingAuthState}>
                {submitting ? 'Verifying code…' : 'Verify SMS code'}
              </button>
            </form>
          ) : null}

          <form className="supportLogin__form" onSubmit={onSubmit}>
            <label className="supportLogin__label">
              <span className="supportLogin__labelText">Email</span>
              <input
                className="supportLogin__input"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </label>

            <label className="supportLogin__label">
              <span className="supportLogin__labelRow">
                <span className="supportLogin__labelText">Password</span>
                <button
                  type="button"
                  className="supportLogin__linkBtn"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </span>
              <input
                className="supportLogin__input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                required
              />
            </label>

            <div className="supportLogin__linkRow">
              <RouterLink className="supportLogin__linkBtn" to={adminPath(ADMIN_PATHS.FORGOT_PASSWORD)}>
                Forgot password?
              </RouterLink>
            </div>

            <button
              className="supportLogin__submit"
              type="submit"
              disabled={submitting || awaitingAuthState || pendingMagicLinkLogin}
            >
              {submitting ? (
                <>
                  <span className="supportLogin__spinner" aria-hidden="true" />
                  Sending verification…
                </>
              ) : (
                pendingMagicLinkLogin ? 'Check verification…' : 'Sign in'
              )}
            </button>
          </form>

          <footer className="supportLogin__footer">
            <button
              type="button"
              className="supportLogin__secondary"
              onClick={() => navigate(adminPath(ADMIN_PATHS.LOGIN))}
            >
              Admin login (security code)
            </button>
          </footer>
        </section>
      </main>
    </div>
  );
};

export default SupportLoginPage;

